import React, { ReactNode, useEffect, useMemo, useState } from 'react'
import {
  GRAY_COLORS_VARIANTS,
  PRIMARY_COLORS_VARIANTS,
  TEXT_DARK_COLORS_VARIANTS,
} from '@/utils/constants/colors'
import { Box, Button, CircularProgress, IconButton, Typography } from '@mui/material'
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import CloseIcon from '@mui/icons-material/Close'
import SendIcon from '@mui/icons-material/Send'

import { MainDrawer } from 'components/shared/MainDrawer'
import { LoadingState } from 'types/prize-draw/prize-draw'
import { userApi } from 'api/userApi'
import { DarkInputField } from '../DarkInputField'

interface ContactUsDrawerProps {
  partnerId: string | number
  defaultName?: string
  defaultEmail?: string
  trigger?: ReactNode
  open?: boolean
  onClose?: () => void
  onOpen?: () => void
}

export const ContactUsDrawer = ({
  partnerId,
  defaultName = '',
  defaultEmail = '',
  trigger,
  open: controlledOpen,
  onClose,
  onOpen,
}: ContactUsDrawerProps) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  const [internalOpen, setInternalOpen] = useState(false)
  const isControlled = controlledOpen !== undefined
  const isOpen = isControlled ? controlledOpen : internalOpen

  const handleClose = () => {
    if (isControlled) {
      onClose?.()
    } else {
      setInternalOpen(false)
      onClose?.()
    }
  }

  const handleOpen = () => {
    if (isControlled) {
      onOpen?.()
    } else {
      setInternalOpen(true)
      onOpen?.()
    }
  }

  const [name, setName] = useState<string>(defaultName)
  const [email, setEmail] = useState<string>(defaultEmail)
  const [message, setMessage] = useState<string>('')
  const [loadingState, setLoadingState] = useState<LoadingState>('idle')
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false)

  useEffect(() => {
    setName(defaultName)
  }, [defaultName])

  useEffect(() => {
    setEmail(defaultEmail)
  }, [defaultEmail])

  const isSendDisabled = useMemo(() => {
    const isEmailValid = emailRegex.test(email.trim())
    return (
      !name.trim() ||
      !email.trim() ||
      !message.trim() ||
      !isEmailValid ||
      loadingState === 'loading'
    )
  }, [name, email, message, loadingState])

  const handleProceed = async () => {
    setIsSubmitted(false)
    setLoadingState('loading')
    try {
      await userApi.postContactUs(partnerId, {
        name: name.trim(),
        email: email.trim(),
        message: message.trim(),
      })
      setMessage('')
      setIsSubmitted(true)
      setLoadingState('idle')
    } catch {
      setLoadingState('error')
    }
  }

  return (
    <MainDrawer
      open={isOpen}
      onClose={handleClose}
      onOpen={handleOpen}
      trigger={
        trigger !== undefined ? (
          trigger
        ) : (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            py={1}
            px={2}
            borderRadius="6px"
            bgcolor="transparent"
            color="inherit"
            sx={{
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: PRIMARY_COLORS_VARIANTS[200],
              },
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <ChatBubbleOutlineOutlinedIcon />
              <Typography sx={{ color: 'inherit' }}>Contact us</Typography>
            </Box>
            <ChevronRightIcon />
          </Box>
        )
      }
    >
      <Box display="flex" flexDirection="column" gap={4} p={2}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box display="flex" flexDirection="column" gap={1}>
            <Typography
              variant="h6"
              component="h1"
              color={TEXT_DARK_COLORS_VARIANTS[400]}
              fontWeight={700}
            >
              Contact us
            </Typography>
            <Typography
              variant="body2"
              component="p"
              color={TEXT_DARK_COLORS_VARIANTS[400]}
              fontSize="0.875rem"
              lineHeight="1rem"
              sx={{ opacity: 0.8 }}
            >
              Send us a message and we'll get back to you as soon as possible.
            </Typography>
          </Box>
          <IconButton
            aria-label="Close contact us drawer"
            size="small"
            onClick={handleClose}
            sx={{ mt: 0.5 }}
          >
            <CloseIcon sx={{ fontSize: '20px' }} />
          </IconButton>
        </Box>
        <DarkInputField
          fullWidth
          inputMode="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Enter your name"
          disabled={loadingState === 'loading'}
        />
        <DarkInputField
          fullWidth
          type="email"
          inputMode="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your email"
          disabled={loadingState === 'loading'}
        />
        {email.trim().length > 0 && !emailRegex.test(email.trim()) && (
          <Typography color="error.main" fontSize="0.875rem" mt={-3}>
            Please enter a valid email address.
          </Typography>
        )}
        <DarkInputField
          fullWidth
          multiline
          rows={5}
          inputMode="text"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Enter your message"
          disabled={loadingState === 'loading'}
        />
        {isSubmitted && loadingState !== 'error' && (
          <Typography color="success.main" fontSize="0.875rem">
            Message sent successfully.
          </Typography>
        )}
        {loadingState === 'error' && (
          <Typography color="error.main" fontSize="0.875rem">
            Unable to send message. Please try again.
          </Typography>
        )}
        <Button
          fullWidth
          variant="contained"
          onClick={handleProceed}
          disabled={isSendDisabled}
          endIcon={
            loadingState === 'loading' ? (
              <CircularProgress size={16} sx={{ color: TEXT_DARK_COLORS_VARIANTS[400] }} />
            ) : (
              <SendIcon color="inherit" />
            )
          }
          sx={{
            py: 1.5,
            borderRadius: '0.75rem',
            fontWeight: 700,
            fontSize: '0.9375rem',
            textTransform: 'none',
            bgcolor: PRIMARY_COLORS_VARIANTS[500],
            color: TEXT_DARK_COLORS_VARIANTS[400],
            boxShadow: 'none',
            '&:hover': { bgcolor: PRIMARY_COLORS_VARIANTS[600], boxShadow: 'none' },
            '&:disabled': {
              bgcolor: GRAY_COLORS_VARIANTS[100],
              color: TEXT_DARK_COLORS_VARIANTS[350],
            },
          }}
        >
          {loadingState === 'loading' ? 'Sending message...' : 'Send Message'}
        </Button>
      </Box>
    </MainDrawer>
  )
}
